/**
 * Home (replaces Dashboard) — the client model of `getHomeFeed` (backend contract: crewdle-sdk-firebase
 * `docs/home-feed-contract.md`, spec HOME-QUESTIONS-SPEC-2026-11-04 §4 Home + §8 "Pick up where you left off").
 *
 * The types mirror the contract; everything else here is pure (the landing rule, the badge count, the "while you
 * were away" collapse, the visit mark, the routes), so the page, the sidebar and the specs share one reading.
 * Money is in credits (100 credits = $1).
 */

// ── The feed (contract §2) ──────────────────────────────────────────────────

export type HomeEmptyReason = 'chat-only' | 'no-agents' | 'quiet';

export interface HomeFeed {
  organizationId: string;
  /** Pass back as markHomeVisited.seenUntilMs. */
  generatedAtMs: number;
  access: { connect: boolean; chat: boolean };
  since: { lastVisitAtMs: number | null; windowStartMs: number; firstVisit: boolean; capped: boolean };
  empty: boolean;
  emptyReason: HomeEmptyReason | null;
  stats: { tasksDone: number; workSavedMinutes: number; spendCredits: number; agentsWorking: number };
  waiting: WaitingItem[];
  away: AwayGroup[];
  goingAhead: GoingAheadItem[];
  working: WorkingItem[];
  /** §6a. Absent on a backend older than §8: read as []. */
  pickUp: PickUpItem[];
  truncated: boolean;
}

export type HomeActionId =
  | 'answer' | 'approve' | 'deny' | 'raise-budget' | 'change-model' | 'add-credits' | 'connect' | 'open' | 'open-questions' | 'ok' | 'change';
export type HomeOpenTarget = { conversationId: string; messageId?: string } | { page: 'questions' | 'billing' | 'agent-settings'; agentId?: string };

export interface HomeAction {
  id: HomeActionId;
  primary?: boolean;
  label?: string;
  callable?: 'answerQuestions' | 'decideAgentApproval' | 'raiseAgentBudget' | 'acknowledgeDefaultedQuestion';
  args?: Record<string, unknown>;
  open?: HomeOpenTarget;
}

export type WaitingKind = 'question' | 'approval' | 'budget' | 'connection' | 'task';

export interface WaitingItem {
  key: string;
  kind: WaitingKind;
  icon: WaitingKind;
  urgency: 0 | 1 | 2 | 3;
  blocking: boolean;
  title: string;
  detail: string;
  projectId: string | null;
  projectName: string | null;
  agentId: string | null;
  agentName: string | null;
  conversationId: string | null;
  messageId: string | null;
  askedAtMs: number;
  actions: HomeAction[];
  question?: { questionId: string; kind: 'blocking' | 'later'; options: string[]; recommended: number | null; allowFreeText: boolean; status: 'open' };
  approval?: { approvalId: string; cardKind: string; status: 'pending' | 'deferred'; severity: 'info' | 'warning' | 'critical' };
  budget?: { reason: 'cap' | 'credits'; capCredits: number | null; suggestedCapCredits: number | null; model: string | null };
  connection?: { cardKind: 'connection' | 'secret'; taskIds: string[] };
  task?: { taskId: string };
}

export type HomeEvent = {
  id: string;
  atMs: number;
  agentId: string | null;
  agentName: string | null;
} & (
  | { type: 'task-done'; taskId: string; title: string; estimatedMinutes: number | null }
  | { type: 'files'; files: Array<{ path: string; name: string }>; fileCount: number }
  | { type: 'publish'; buildProjectId: string; versionName: string; liveUrl: string; action: 'publish' | 'restore';
    publishedBy: { kind: 'agent' | 'owner' | 'platform'; agentId?: string; uid?: string } }
  | { type: 'problem'; problem: 'not-delivered' | 'agent-error' | 'unreachable'; taskId?: string; title: string }
  | { type: 'budget'; budget: 'cap-reached' | 'out-of-credits' }
);

export interface AwayGroup {
  key: string;
  kind: 'project' | 'agent';
  projectId: string | null;
  agentId: string | null;
  conversationId: string;
  name: string;
  agentCount: number;
  spendCredits: number;
  eventCount: number;
  events: HomeEvent[];
  recap: { text: string; generatedAtMs: number } | null;
  lastEventAtMs: number;
}

export interface GoingAheadItem {
  questionId: string;
  status: 'open' | 'defaulted';
  title: string;
  choice: string;
  projectId: string | null;
  projectName: string | null;
  agentName: string;
  conversationId: string;
  messageId: string;
  deadlineAtMs: number | null;
  decidedAtMs: number | null;
  actions: HomeAction[];
}

export interface WorkingItem {
  agentId: string;
  agentName: string;
  model: string | null;
  projectId: string | null;
  projectName: string | null;
  conversationId: string | null;
  taskId: string | null;
  taskTitle: string | null;
  startedAtMs: number;
  costTodayCredits: number;
  fastSpend: { ratePerHourCredits: number; activeHours: number } | null;
}

/** §6a "Pick up where you left off". */
export interface PickUpItem {
  key: string;
  kind: 'project' | 'agent' | 'chat';
  projectId: string | null;
  agentId: string | null;
  chatId: string | null;
  conversationId: string | null;
  name: string;
  lastActivityAtMs: number;
  reason: 'next-step' | 'question' | 'request';
  suggestion: { text: string; resumeText: string; source: 'ai' | 'fallback'; generatedAtMs: number };
  spendCredits: number;
  deliverableCount: number;
}

export type PickUpAction = 'resume' | 'snooze' | 'done' | 'undo';

export interface UpdatePickUpResult {
  itemKey: string;
  state: 'snoozed' | 'done' | null;
  untilMs: number | null;
  resumeText: string | null;
}

/** A defensive read of the callable's answer: lists default to [] (an older backend has no `pickUp`). */
export function normalizeFeed(raw: Partial<HomeFeed> & { organizationId: string }): HomeFeed {
  return {
    organizationId: raw.organizationId,
    generatedAtMs: typeof raw.generatedAtMs === 'number' ? raw.generatedAtMs : Date.now(),
    access: raw.access ?? { connect: true, chat: true },
    since: raw.since ?? { lastVisitAtMs: null, windowStartMs: Date.now() - 86_400_000, firstVisit: true, capped: false },
    empty: raw.empty === true,
    emptyReason: raw.emptyReason ?? null,
    stats: raw.stats ?? { tasksDone: 0, workSavedMinutes: 0, spendCredits: 0, agentsWorking: 0 },
    waiting: raw.waiting ?? [],
    away: raw.away ?? [],
    goingAhead: raw.goingAhead ?? [],
    working: raw.working ?? [],
    pickUp: raw.pickUp ?? [],
    truncated: raw.truncated === true,
  };
}

// ── Landing: Home, or a new chat when Home is empty (spec §1 "Empty Home → Chat", §8, decision 9) ──

/**
 * Where a navigation into Home came from counts as "opening the app": no previous navigation (a cold start, the
 * Microsoft Teams tab loading `/` → `/login` → Home), or a sign-in / hand-off page (login, the auth bridge, Teams
 * SSO, onboarding). A click on Home in the rail is not a landing: it always shows Home.
 */
const LANDING_PAGES = ['login', 'auth-start', 'auth-end', 'teams-no-access', 'email-action', 'onboarding', 'region-unavailable', 'product-disabled', 'error'];
const LANDING_SOURCES = new RegExp(`^/(?:o/[^/]+/)?(?:${LANDING_PAGES.join('|')})(?:[/?#]|$)`);

export function isLandingNavigation(previousUrl: string | null | undefined): boolean {
  if (!previousUrl) return true;
  return LANDING_SOURCES.test(previousUrl);
}

/**
 * Empty → Chat: the feed says empty (chat-only role, no agents, or nothing at all) AND it is the landing AND the
 * person has Chat. "Pick up" items alone are something worth showing (spec §8): they keep Home even if an older
 * feed still says empty. Pure.
 */
export function shouldOpenChatInstead(feed: Pick<HomeFeed, 'empty' | 'access' | 'pickUp'>, landing: boolean): boolean {
  if (!landing || !feed.empty) return false;
  if ((feed.pickUp ?? []).length > 0) return false;
  return feed.access?.chat !== false;
}

// ── Sidebar badge ───────────────────────────────────────────────────────────

/**
 * The Home row's live count = "Waiting on you": every card and task waiting on the person (NeedsYouService: approvals,
 * connections, budget/credits holds as tasks) + open registry questions that need an answer (blocking / can wait;
 * defaults go ahead on their own, they are not counted). Pure.
 */
export function homeBadgeCount(needsYouCount: number, openQuestions: ReadonlyArray<{ status: string; kind: string }>): number {
  const questions = openQuestions.filter((q) => q.status === 'open' && q.kind !== 'default').length;
  return Math.max(0, needsYouCount) + questions;
}

// ── "Since your last visit" / markHomeVisited (contract §1) ─────────────────

/** A view of Home counts only after this long (the server ignores shorter ones too). */
export const MIN_HOME_VIEW_MS = 5_000;

/** The markHomeVisited request for a view that lasted `viewedMs`, or null when it is too short to count. Pure. */
export function visitMark(organizationId: string, generatedAtMs: number, viewedMs: number):
  { organizationId: string; seenUntilMs: number; viewedMs: number } | null {
  if (!organizationId || !Number.isFinite(generatedAtMs) || viewedMs < MIN_HOME_VIEW_MS) return null;
  return { organizationId, seenUntilMs: generatedAtMs, viewedMs: Math.round(viewedMs) };
}

/** The viewer's local midnight (getHomeFeed `dayStartMs`, "cost today"). */
export function localDayStartMs(now: number = Date.now()): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export type GreetingKey = 'morning' | 'afternoon' | 'evening';

export function greetingKey(now: number = Date.now()): GreetingKey {
  const hour = new Date(now).getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

// ── While you were away (contract §3) ───────────────────────────────────────

/** Lines a project shows before "Show N more". */
export const AWAY_COLLAPSED_LINES = 3;

export function visibleEvents(group: Pick<AwayGroup, 'events'>, expanded: boolean): HomeEvent[] {
  return expanded ? group.events : group.events.slice(0, AWAY_COLLAPSED_LINES);
}

/** "Show N more updates": every event of the window past the first three (eventCount may exceed events.length). */
export function hiddenEventCount(group: Pick<AwayGroup, 'events' | 'eventCount'>, expanded: boolean): number {
  if (expanded) return Math.max(0, group.eventCount - group.events.length);
  return Math.max(0, Math.max(group.eventCount, group.events.length) - AWAY_COLLAPSED_LINES);
}

// ── Durations ───────────────────────────────────────────────────────────────

/** "25 min", "3 h 10 min", "2 h". */
export function durationParts(ms: number): { hours: number; minutes: number } {
  const total = Math.max(0, Math.round(ms / 60_000));
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

export function minutesParts(minutes: number): { hours: number; minutes: number } {
  return durationParts(Math.max(0, minutes) * 60_000);
}

// ── Routes ──────────────────────────────────────────────────────────────────

export interface HomeRouteContext {
  /** Project (team) spaces and org spaces: they open under /messaging. */
  spaceConversationIds: ReadonlySet<string>;
}

export interface HomeRoute {
  commands: string[];
  queryParams: Record<string, string>;
}

/** A conversation (optionally a message in it): a space under /messaging, an agent chat under /agents (like NeedsYou). */
export function conversationRoute(conversationId: string, messageId: string | null | undefined, isSpace: boolean): HomeRoute {
  const queryParams: Record<string, string> = { conversationId };
  if (messageId) queryParams.messageId = messageId;
  return { commands: [isSpace ? '/messaging' : '/agents'], queryParams };
}

/** Where a waiting item's conversation is: a project item is in its space. */
export function itemIsSpace(item: { projectId: string | null; conversationId: string | null }, ctx: HomeRouteContext): boolean {
  return !!item.conversationId && (!!item.projectId || ctx.spaceConversationIds.has(item.conversationId));
}

/** Where a "Pick up" item opens: a chat at /chat/c/{id}; a project in its space; an agent in its chat. */
export function pickUpRoute(item: Pick<PickUpItem, 'kind' | 'chatId' | 'conversationId'>): HomeRoute | null {
  if (item.kind === 'chat') return item.chatId ? { commands: ['/chat/c', item.chatId], queryParams: {} } : null;
  if (!item.conversationId) return null;
  return conversationRoute(item.conversationId, null, item.kind === 'project');
}

/** The composer a "Pick up" Resume fills (CoachActivityService surface keys: `conv:` / `chat:`). */
export function pickUpComposerKey(item: Pick<PickUpItem, 'kind' | 'chatId' | 'conversationId'>): string | null {
  if (item.kind === 'chat') return item.chatId ? `chat:${item.chatId}` : null;
  return item.conversationId ? `conv:${item.conversationId}` : null;
}

/** The primary action of an item (at most one per the contract), else the first. */
export function primaryAction(actions: readonly HomeAction[]): HomeAction | null {
  return actions.find((a) => a.primary) ?? actions[0] ?? null;
}
